import type { IShortcutListSection } from '../../contexts/AppShortcutsContext'
import { hasCommandModifier } from '../../helpers/keyboardShortcuts'
import { commandKeycap, isMacPlatform } from '../../helpers/platform'

/**
 * One name per thing the surface answers a keystroke with.
 *
 * Every member is listed in `MESSAGES_SHORTCUTS` below, which is both what the
 * handlers match against and what the shortcuts sheet renders — so the sheet
 * cannot advertise a key nothing answers to, or omit one that works.
 */
export enum MessagesShortcut {
  AskArc = 'askArc',
  JumpToConversation = 'jumpToConversation',
  SearchMessages = 'searchMessages',
  NewConversation = 'newConversation',
  StepConversation = 'stepConversation',
  StepUnread = 'stepUnread',
  BackToComposer = 'backToComposer',
  EditLastMessage = 'editLastMessage',
  AttachFile = 'attachFile',
  AddPeople = 'addPeople',
  MuteVoiceCall = 'muteVoiceCall',
}

/** The fields of a keydown both the DOM and React's synthetic event carry. */
export interface IKeyChord {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}

export interface IMessagesShortcut {
  id: MessagesShortcut
  /**
   * Keycaps as the sheet prints them, left to right. `COMMAND_KEYCAP` stands in
   * for ⌘ / Ctrl so one entry reads correctly on either platform.
   */
  keys: string[]
  label: string
  matches: (event: IKeyChord) => boolean
}

/**
 * ⌘ on a Mac, Ctrl everywhere else. A label only — every matcher below accepts
 * either modifier, the way the rest of the app's chords always have.
 */
const COMMAND_KEYCAP = commandKeycap()
/** ⌥ is what a Mac keyboard prints; the same physical key is Alt elsewhere. */
const ALT_KEYCAP = isMacPlatform() ? '⌥' : 'Alt'

/** A chord of the command modifier plus a letter, with Alt reserved for others. */
const isCommandChord = (event: IKeyChord, letter: string, withShift: boolean): boolean =>
  hasCommandModifier(event) &&
  !event.altKey &&
  event.shiftKey === withShift &&
  event.key.toLowerCase() === letter

/**
 * ⌘J / Ctrl+J. Exported because the chat bubble answers it on every other page,
 * before the AIM surface is mounted to.
 */
export const isAskArcChord = (event: IKeyChord): boolean => isCommandChord(event, 'j', false)

/** ⌘⇧K / Ctrl+Shift+K. Exported for the same reason as `isAskArcChord`. */
export const isNewConversationChord = (event: IKeyChord): boolean =>
  isCommandChord(event, 'k', true)

/**
 * ⌘⇧Space / Ctrl+Shift+Space, the chord Slack huddles mute on. Answered by
 * ArcVoiceCallProvider, since a live call carries on across every page, not only this one.
 */
export const isMuteVoiceCallChord = (event: IKeyChord): boolean =>
  hasCommandModifier(event) && event.shiftKey && !event.altKey && event.key === ' '

/**
 * Everything AIM answers to, in the order the shortcuts sheet lists it.
 *
 * Two consumers, deliberately not one: the window handler on the Messages page
 * owns everything that acts on the surface, and `EditLastMessage` is answered by
 * MessageComposer, which is the only thing that knows whether the box it is
 * typing into is empty. Both match through this array, so the split is about
 * which component acts, never about which keys exist.
 */
export const MESSAGES_SHORTCUTS: IMessagesShortcut[] = [
  {
    id: MessagesShortcut.AskArc,
    keys: [COMMAND_KEYCAP, 'J'],
    label: 'Ask Arc',
    matches: (event) => isAskArcChord(event),
  },
  {
    id: MessagesShortcut.JumpToConversation,
    keys: [COMMAND_KEYCAP, 'K'],
    label: 'Search, jump to a conversation, or ask Arc',
    matches: (event) => isCommandChord(event, 'k', false),
  },
  {
    id: MessagesShortcut.SearchMessages,
    keys: [COMMAND_KEYCAP, 'Shift', 'F'],
    label: 'Search messages',
    matches: (event) => isCommandChord(event, 'f', true),
  },
  {
    id: MessagesShortcut.NewConversation,
    // Not ⌘⇧N: Chrome and Edge open an incognito window on it, Safari a private
    // one and Firefox undoes a closed window — none of them deliver it to the
    // page, so the sheet would advertise a chord nothing answers to. ⌘⇧K is
    // unreserved and is what Slack binds the same action to.
    keys: [COMMAND_KEYCAP, 'Shift', 'K'],
    label: 'New conversation',
    matches: (event) => isNewConversationChord(event),
  },
  {
    id: MessagesShortcut.StepConversation,
    // One row for a pair: they are one movement in two directions, and printing
    // them separately would make the sheet list seven things for six shortcuts.
    keys: [ALT_KEYCAP, '↑ / ↓'],
    label: 'Previous / next conversation',
    matches: (event) =>
      event.altKey &&
      !hasCommandModifier(event) &&
      !event.shiftKey &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown'),
  },
  {
    id: MessagesShortcut.StepUnread,
    keys: [ALT_KEYCAP, 'Shift', '↑ / ↓'],
    label: 'Previous / next unread conversation',
    matches: (event) =>
      event.altKey &&
      !hasCommandModifier(event) &&
      event.shiftKey &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown'),
  },
  {
    // Escape is answered before matching (see `runMessagesShortcut`); listed so
    // the sheet says where it now takes the caret.
    id: MessagesShortcut.BackToComposer,
    keys: ['Esc'],
    label: 'Close the side panel, or go back to the message box',
    matches: (event) => event.key === 'Escape',
  },
  {
    id: MessagesShortcut.EditLastMessage,
    keys: ['↑'],
    label: 'Edit your last message',
    matches: (event) =>
      event.key === 'ArrowUp' && !hasCommandModifier(event) && !event.shiftKey && !event.altKey,
  },
  {
    id: MessagesShortcut.AttachFile,
    keys: [COMMAND_KEYCAP, 'Shift', 'U'],
    label: 'Attach a file',
    matches: (event) => isCommandChord(event, 'u', true),
  },
  {
    // A command typed into the message box, read on send like `/giphy` — so no
    // keystroke matches it; the entry is here so the sheet lists it.
    id: MessagesShortcut.AddPeople,
    keys: ['/add'],
    label: 'Add people (type it in the message box)',
    matches: () => false,
  },
  {
    id: MessagesShortcut.MuteVoiceCall,
    keys: [COMMAND_KEYCAP, 'Shift', 'Space'],
    label: 'Mute or unmute your mic on an Arc Voice call',
    matches: (event) => isMuteVoiceCallChord(event),
  },
]

/**
 * The keycaps for one shortcut, for the places that name a single chord in prose
 * rather than listing them all — so a Mac's ⌘ and a PC's Ctrl stay right there too.
 */
export const shortcutKeys = (id: MessagesShortcut): string[] =>
  MESSAGES_SHORTCUTS.find((shortcut) => shortcut.id === id)?.keys ?? []

/** One chord as inline text — `⌘J` on a Mac, `Ctrl+J` elsewhere — for tooltips and hints. */
export const shortcutLabel = (id: MessagesShortcut): string =>
  shortcutKeys(id).join(isMacPlatform() ? '' : '+')

/** What the AIM surface adds to the shortcut sheet while it is mounted, page or bubble. */
export const MESSAGES_SHORTCUT_SECTION: IShortcutListSection = {
  title: 'AIM',
  shortcuts: MESSAGES_SHORTCUTS.map(({ id, keys, label }) => ({ id, label, chords: [keys] })),
}

/** Which shortcut this keystroke is, or null when it is just a keystroke. */
export const matchMessagesShortcut = (event: IKeyChord): MessagesShortcut | null =>
  MESSAGES_SHORTCUTS.find((shortcut) => shortcut.matches(event))?.id ?? null

/** Which way `StepConversation` was pressed. */
export const stepDirection = (event: IKeyChord): -1 | 1 => (event.key === 'ArrowUp' ? -1 : 1)

const asElement = (target: EventTarget | null): Element | null =>
  target instanceof Element ? target : null

/**
 * True while a modal, menu or popover owns the keyboard.
 *
 * MUI traps focus inside whatever it opens, so the keystroke's own target is the
 * reliable test — and it covers every overlay on the surface at once rather than
 * the page having to enumerate which of its dialogs happen to be open. Without
 * it, ⌥↑ typed into a dialog would step the conversation list underneath it.
 */
export const isKeyEventInsideOverlay = (event: { target: EventTarget | null }): boolean =>
  !!asElement(event.target)?.closest('.MuiModal-root')

/**
 * The keystroke came from a control rather than from the text beside it — a
 * toolbar button holding focus, not the caret. Every control on the composer's
 * row is a `<button>`, so Tab-ing to Bold and pressing ↑ is not "↑ in an empty
 * message box".
 */
export const isKeyEventOnControl = (event: { target: EventTarget | null }): boolean =>
  !!asElement(event.target)?.closest('button')

const closestEditor = (target: EventTarget | null): HTMLElement | null =>
  asElement(target)?.closest<HTMLElement>('[contenteditable="true"]') ?? null

/**
 * The caret is in a rich-text editor with something selected — so ⌘K means "link
 * this", which is `isLinkShortcut` in editorKeyHandlers, and the page stands down.
 *
 * Read off the live DOM selection rather than Draft's, because the window handler
 * has no editor state to consult: a Draft selection *is* a DOM selection, so the
 * two agree.
 */
export const hasEditorSelection = (event: { target: EventTarget | null }): boolean => {
  if (!closestEditor(event.target)) return false
  const selection = window.getSelection()
  return !!selection && !selection.isCollapsed
}

/**
 * Set on the composer's editor wrapper while there is anything in it at all.
 *
 * Text is only one of the three things a half-written message can be: a GIF is an
 * atomic block that leaves no text behind, and staged attachments are not in the
 * editor's DOM in the first place. Neither is visible to a window handler reading
 * `textContent`, so the composer — which already has to compute this for ↑ —
 * publishes the answer on the element the keystroke comes from. Same contract as
 * `KEYBOARD_OPEN_ATTRIBUTE`: one fact, written where a listener can read it.
 */
export const COMPOSER_CONTENT_ATTRIBUTE = 'data-composer-has-content'

/**
 * The caret is somewhere with content in it, where an arrow key is cursor movement
 * first — ⌥↑/⌥↓ moves by paragraph on macOS, so stepping conversations out from
 * under a half-written message would be taking the key off its owner.
 *
 * An empty box has nothing to move through, which is what lets the same chords
 * work from a composer nobody has typed in yet.
 */
export const isCaretInTextWithContent = (event: { target: EventTarget | null }): boolean => {
  const target = event.target
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return target.value.length > 0
  }
  const element = asElement(target)
  // The composer's own answer, which counts the GIF and the staged file that
  // `textContent` below cannot see.
  if (element?.closest(`[${COMPOSER_CONTENT_ATTRIBUTE}]`)) return true
  const editor = closestEditor(target)
  return !!editor?.textContent?.trim()
}

/**
 * The keystroke belongs to this surface: it came from inside it, or from nothing
 * in particular — the body, with no field focused.
 *
 * Only ⌥↑/⌥↓ asks. `MessagesBubble` mounts this whole surface over every other
 * page in the app, and Alt+Arrow is a live gesture in fields that are not ours —
 * Alt+↓ is how a MUI Autocomplete opens its list — so without this an empty input
 * on the host page would lose the key and switch the bubble's conversation
 * underneath the reader. The chords that mean nothing anywhere else keep the
 * global reach they have always had.
 */
export const isKeyEventForSurface = (
  event: { target: EventTarget | null },
  surface: Element | null,
): boolean => {
  const element = asElement(event.target)
  if (!element || element === document.body || element === document.documentElement) return true
  return !!surface?.contains(element)
}

/** Everything the dispatcher below needs off a keystroke, DOM or React synthetic. */
export type IShortcutEvent = IKeyChord & {
  target: EventTarget | null
  preventDefault: () => void
}

/**
 * What the surface does with each chord. One per shortcut it answers, plus the
 * surface root that ⌥↑/⌥↓ is scoped to.
 *
 * `EditLastMessage` is absent on purpose: MessageComposer answers it, because
 * "the box is empty" is the whole condition and only it knows.
 */
export interface IMessagesShortcutHandlers {
  /** Gets the keystroke so the page can tell its own Escape from a host page's. */
  onDismiss: (event: { target: EventTarget | null }) => void
  onAskArc: () => void
  onJumpToConversation: () => void
  onSearchMessages: () => void
  onNewConversation: () => void
  onStepConversation: (step: -1 | 1) => void
  onStepUnread: (step: -1 | 1) => void
  onAttachFile: () => void
  surface: Element | null
}

/** What a keystroke turned into: a shortcut, a dismiss, or nothing. */
export type MessagesKeyAction = MessagesShortcut | 'dismiss' | null

/**
 * Turn one keystroke on the surface into the one thing it means.
 *
 * The dispatch table lives here rather than inline in the page so that which
 * chord runs which handler — and, more importantly, the carve-outs that make each
 * one stand down — can be tested without mounting a 3000-line connected page.
 * Returns what it claimed, which is for the tests; the page ignores it.
 *
 * There is deliberately no blanket "are you typing" guard. A chord inserts
 * nothing, and the caret spends most of its life in the composer, the search box
 * or the sidebar filter, so guarding those was the same as not having the
 * shortcuts at all. What replaces it is the rule applied where it actually bites:
 * the editor wins when the keystroke already means something to it.
 */
export const runMessagesShortcut = (
  event: IShortcutEvent,
  handlers: IMessagesShortcutHandlers,
): MessagesKeyAction => {
  // A dialog, menu or popover owns the keyboard while it is open, so its own keys
  // never also act on the surface behind it.
  if (isKeyEventInsideOverlay(event)) return null

  // Escape is exempt from every carve-out, as it always has been: dismissing is
  // always safe. Its `BackToComposer` entry exists only to list it on the sheet.
  if (event.key === 'Escape') {
    handlers.onDismiss(event)
    return 'dismiss'
  }

  const shortcut = matchMessagesShortcut(event)
  switch (shortcut) {
    case MessagesShortcut.AskArc:
      event.preventDefault()
      handlers.onAskArc()
      return shortcut
    case MessagesShortcut.JumpToConversation:
      // The link popover claims this chord whenever there is a selection to link
      // (`isLinkShortcut` in editorKeyHandlers is exactly it).
      if (hasEditorSelection(event)) return null
      event.preventDefault()
      handlers.onJumpToConversation()
      return shortcut
    case MessagesShortcut.SearchMessages:
      event.preventDefault()
      handlers.onSearchMessages()
      return shortcut
    case MessagesShortcut.NewConversation:
      event.preventDefault()
      handlers.onNewConversation()
      return shortcut
    case MessagesShortcut.StepConversation:
      // Yields to the caret whenever the box it is in holds anything: on macOS
      // ⌥↑/⌥↓ moves by paragraph.
      if (isCaretInTextWithContent(event)) return null
      if (!isKeyEventForSurface(event, handlers.surface)) return null
      event.preventDefault()
      handlers.onStepConversation(stepDirection(event))
      return shortcut
    case MessagesShortcut.StepUnread:
      // Same carve-outs as stepping: ⌥⇧↑/↓ extends a text selection by paragraph.
      if (isCaretInTextWithContent(event)) return null
      if (!isKeyEventForSurface(event, handlers.surface)) return null
      event.preventDefault()
      handlers.onStepUnread(stepDirection(event))
      return shortcut
    case MessagesShortcut.AttachFile:
      event.preventDefault()
      handlers.onAttachFile()
      return shortcut
    default:
      // `EditLastMessage` included: answered by the composer, not here. `MuteVoiceCall` is the call's.
      return null
  }
}
